import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const SEOExpert = () => {
  const testimonials = [
    {
      id: 1,
      name: "Radhika Sharma",
      review:
        "This SEO expert course is a game-changer! I learned practical strategies for keyword research, on-page optimization, and link building. My website's traffic has significantly improved. Highly recommended in Noida!",
      rating: 5,
      date: "2024-06-20",
      profileImg: "https://randomuser.me/api/portraits/women/30.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Deepak Verma",
      review:
        "The trainers are incredibly knowledgeable and explain complex SEO concepts in an easy-to-understand way. I gained hands-on experience with various SEO tools and confidently manage campaigns now.",
      rating: 4,
      date: "2024-06-18",
      profileImg: "https://randomuser.me/api/portraits/men/40.jpg", // Placeholder image
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Priya Singh",
      review:
        "From technical SEO to local SEO, every module was packed with valuable insights. The live project helped me implement strategies and see real results. I got a great job as an SEO Analyst post-training.",
      rating: 5,
      date: "2024-06-15",
      profileImg: "https://randomuser.me/api/portraits/women/50.jpg", // Placeholder image
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Manish Kumar",
      review:
        "The SEO course provided a complete overview of search engine algorithms and ranking factors. The practical sessions on Google Analytics and Search Console were particularly useful. Truly an expert-level course.",
      rating: 5,
      date: "2024-06-12",
      profileImg: "https://randomuser.me/api/portraits/men/60.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Aisha Khan",
      review:
        "This program is perfect for anyone looking to make a career in digital marketing. The SEO module was thorough, with actionable tips and constant support from the instructors. My skills are now industry-ready.",
      rating: 4,
      date: "2024-06-10",
      profileImg: "https://randomuser.me/api/portraits/women/70.jpg", // Placeholder image
      source: "LinkedIn",
    },
  ];

   const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const faqContent = [
    {
      title: "What is SEO and why is it crucial for businesses?",
      answer:
        "SEO (Search Engine Optimization) is the practice of increasing the quantity and quality of traffic to your website through organic search engine results. It's crucial because it helps businesses gain visibility, attract relevant visitors, establish authority, and ultimately drive conversions without paid advertising.",
    },
    {
      title: "What are the prerequisites for joining this SEO Expert course?",
      answer:
        "There are no strict technical prerequisites. A basic understanding of how websites work and familiarity with the internet is helpful. This course is designed for aspiring digital marketers, business owners, content creators, and anyone looking to boost online visibility.",
    },
    {
      title: "What key SEO concepts will I learn in this course?",
      answer:
        "You will learn about **Keyword Research**, **On-Page SEO**, **Off-Page SEO (Link Building)**, **Technical SEO**, Local SEO, Google Algorithms, SEO Audits, Competitor Analysis, Content SEO, Mobile SEO, and Web Analytics (Google Analytics, Google Search Console).",
    },
    {
      title: "Is SEO still relevant in today's digital landscape?",
      answer:
        "Absolutely! Despite advancements in AI and other marketing channels, SEO remains fundamental. Search engines are the primary source of information for most internet users. Mastering SEO ensures long-term organic visibility and sustainable growth for any online presence.",
    },
    {
      title: "Will I get placement assistance after course completion?",
      answer:
        "Yes, we provide 100% placement assistance for our SEO graduates. This includes professional resume building, mock interview preparation, soft skills training, and connecting you with our network of digital marketing agencies and companies for roles like SEO Analyst, SEO Executive, and Digital Marketing Specialist in Noida and across India.",
    },
    {
      title: "What kind of practical experience will I gain?",
      answer:
        "You will work on live projects, performing SEO audits for real websites, conducting keyword research for various niches, optimizing on-page elements, developing link-building strategies, and analyzing performance using Google Analytics and Search Console. This hands-on experience is crucial for becoming an SEO expert.",
    },
    {
      title: "What are the career opportunities after this SEO training?",
      answer:
        "Upon successful completion, you can pursue roles such as SEO Specialist, SEO Analyst, Digital Marketing Executive, Content Strategist, Link Building Specialist, Technical SEO Expert, or even work as a freelance SEO consultant.",
    },
    {
      title: "What is the duration and mode of training?",
      answer:
        "The course duration varies based on the pace (e.g., intensive fast-track vs. regular batch). We offer both immersive offline classroom training at our Noida center and interactive live online classes, providing flexibility to suit your learning preference.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: Introduction to Search Engine Optimization (SEO)",
      description: "Understanding how search engines work, SEO vs. SEM, types of SEO, and the importance of organic rankings for businesses.",
    },
    {
      title: "Module 2: Keyword Research & Analysis",
      description: "Identifying relevant keywords, long-tail keywords, keyword intent, using tools like Google Keyword Planner, SEMrush, and Ahrefs.",
    },
    {
      title: "Module 3: On-Page SEO Optimization",
      description: "Optimizing website content, meta titles, meta descriptions, header tags, image optimization, URL structure, and content readability.",
    },
    {
      title: "Module 4: Technical SEO Fundamentals",
      description: "Understanding website architecture, crawlability, indexability, sitemaps, robot.txt, canonicalization, and site speed optimization.",
    },
    {
      title: "Module 5: Off-Page SEO (Link Building)",
      description: "Strategies for acquiring high-quality backlinks, understanding domain authority, link types (dofollow/nofollow), and outreach techniques.",
    },
    {
      title: "Module 6: Local SEO & Google My Business",
      description: "Optimizing for local searches, setting up and managing Google My Business profiles, local citations, and reviews management.",
    },
    {
      title: "Module 7: Content SEO & Marketing",
      description: "Creating SEO-friendly content, content clusters, understanding E-E-A-T principles, and integrating SEO into content strategy.",
    },
    {
      title: "Module 8: Google Algorithms & Penalties",
      description: "Understanding major Google algorithm updates (Panda, Penguin, Hummingbird, Core Updates) and how to recover from penalties.",
    },
    {
      title: "Module 9: SEO Auditing & Reporting",
      description: "Conducting comprehensive SEO audits, identifying issues, providing recommendations, and creating performance reports for clients.",
    },
    {
      title: "Module 10: Web Analytics & Tools (Google Analytics, Search Console)",
      description: "Setting up and interpreting data from Google Analytics and Google Search Console to monitor performance, traffic, and crawl errors.",
    },
    {
      title: "Module 11: Competitive Analysis & Advanced Strategies",
      description: "Analyzing competitor SEO strategies, finding untapped opportunities, and advanced techniques like schema markup and voice search optimization.",
    },
    {
      title: "Module 12: Live Project & Case Studies",
      description: "Apply all learned SEO strategies on a real website or client project, analyze results, and present findings. This builds a strong portfolio.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(startIndex + i) % testimonials.length]
    );
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>SEO Expert Course in Noida | Master Search Engine Optimization</title>
        <meta
          name="description"
          content="Become a certified SEO Expert in Noida. Our comprehensive training covers Keyword Research, On-Page, Off-Page, Technical SEO, and Analytics for top rankings with placement support."
        />
        <meta
          name="keywords"
          content="SEO expert course Noida, SEO training, learn SEO, Search Engine Optimization, Digital Marketing, Keyword Research, On-Page SEO, Off-Page SEO, Technical SEO, Google Analytics, Google Search Console, SEO certification, top SEO course"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://static.vecteezy.com/system/resources/thumbnails/027/303/354/small_2x/business-people-use-seo-tools-unlocking-online-potential-boost-visibility-attract-organic-traffic-and-dominate-search-engine-rankings-with-strategic-optimization-techniques-digital-marketing-photo.jpg"
          alt="SEO Expert Course Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
            >
              Become an <span className="text-orange-600">SEO Expert</span> in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200 text-left"
            >
              Master the art of search engine optimization to drive organic traffic and achieve top rankings for any website.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-orange-700 hover:bg-orange-500 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
             <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("Full Stack Development Training")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Why Learn SEO Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Become an SEO Expert?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            SEO is the backbone of organic digital marketing. As an SEO expert, you'll be instrumental in helping businesses thrive online, enhancing their visibility, increasing website traffic, and generating qualified leads without relying solely on paid ads.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-purple-700 mb-3">High Demand Skill</h3>
              <p className="text-gray-600">Businesses constantly need SEO specialists to improve online presence.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-purple-700 mb-3">Sustainable Growth</h3>
              <p className="text-gray-600">Drive long-term organic traffic and consistent business growth.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-purple-700 mb-3">Flexible Career Paths</h3>
              <p className="text-gray-600">Work in-house, at agencies, or as a successful freelance consultant.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🔍', title: 'Comprehensive SEO Modules', desc: 'Covering On-Page, Off-Page, Technical, Local, and Content SEO.' },
              { icon: '📈', title: 'Practical, Hands-on Experience', desc: 'Work on live projects and real-world case studies for practical application.' },
              { icon: '👨‍🏫', title: 'Industry Expert Trainers', desc: 'Learn from seasoned SEO professionals with proven track records.' },
              { icon: '🏆', title: 'Certification & Portfolio Building', desc: 'Earn a recognized certificate and build a strong SEO portfolio.' },
              { icon: '🤝', title: '100% Placement Assistance', desc: 'Dedicated support for resume building, mock interviews, and job placements.' },
              { icon: '📊', title: 'Master Analytics & Tools', desc: 'Proficiency in Google Analytics, Search Console, SEMrush, Ahrefs, etc.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-indigo-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-indigo-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Platforms */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Platforms You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our SEO expert training provides hands-on experience with the most essential tools used by industry professionals.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'Google Analytics', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg', desc: 'Analyze website traffic & user behavior' },
              { name: 'Google Search Console', logo: 'https://www.biz2byte.com/wp-content/uploads/2023/11/Design-ohne-Titel-1.png', desc: 'Monitor search performance & crawl issues' },
              { name: 'Google Keyword Planner', logo: 'https://i.pinimg.com/736x/16/6a/0c/166a0c5c38a1c9dea994481bb2847f31.jpg', desc: 'Discover new keywords' },
              { name: 'SEMrush', logo: 'https://e7.pngegg.com/pngimages/343/371/png-clipart-semrush-white-logo-tech-companies-thumbnail.png', desc: 'Comprehensive SEO tool' },
              { name: 'Ahrefs', logo: 'https://www.pngall.com/wp-content/uploads/16/Ahrefs-SEO-Tool-Logo-PNG-File.png', desc: 'Powerful backlink analysis' },
              { name: 'Screaming Frog SEO Spider', logo: 'https://images.icon-icons.com/3053/PNG/512/screaming_frog_seo_spider_macos_bigsur_icon_189769.png', desc: 'Technical SEO auditing' },
              { name: 'Yoast SEO (WordPress)', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/wordpress/wordpress-plain.svg', desc: 'On-page optimization plugin' },
              { name: 'Moz (MozBar)', logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ2SvaQIIIAOS-kXrAVGev-hU72Nql94d8A_Q&s', desc: 'Domain Authority metrics' },
              { name: 'PageSpeed Insights', logo: 'https://www.dzinsights.com/services/blog-manager/public/BLOG/460d0eb8-3436-4fd7-b71b-9d4886a21629.webp', desc: 'Website speed analysis' },
              { name: 'Ubersuggest', logo: 'https://cdn.prod.website-files.com/67aaec40eca5cd4a41589f8b/67dbadbe186cec74322fb94a_ubersuggest.webp', desc: 'Keyword & content ideas' },
        
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2 object-contain" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
                {tool.desc && <p className="text-gray-500 text-xs mt-1">{tool.desc}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-indigo-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities in SEO</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A certification as an **SEO Expert** from our program opens up diverse and lucrative career paths in the rapidly growing digital marketing industry.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '📈', title: 'SEO Specialist / Analyst', desc: 'Implement and analyze SEO strategies to improve search rankings.' },
              { icon: '✍️', title: 'Content Strategist', desc: 'Develop content plans optimized for search engines and user engagement.' },
              { icon: '🔗', title: 'Link Building Specialist', desc: 'Acquire high-quality backlinks to boost website authority.' },
              { icon: '👩‍💻', title: 'Technical SEO Expert', desc: 'Focus on website structure, crawlability, and performance for search engines.' },
              { icon: '📊', title: 'Digital Marketing Manager', desc: 'Oversee overall digital marketing campaigns, with SEO as a core component.' },
              { icon: '💡', title: 'Freelance SEO Consultant', desc: 'Offer your expertise to multiple clients, managing their organic growth.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-indigo-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-gray-100 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our SEO Expert training program.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., SEO Expert Course)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name (Optional)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-purple-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-purple-900">
            What Our SEO Expert Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-purple-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date} • {testimonial.source}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-purple-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-purple-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

       {/* Conditionally render the popup */}
            {isPopupOpen && (
              <JoinNowFormPopup
                onClose={handleClosePopup}
                courseTitle={selectedCourseTitle}
              />
            )}
    </div>
  );
};

export default SEOExpert;